import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

interface Coordinates {
    lat: number;
    lng: number;
}

// Define interaction states
type MapInteractionState = 'idle' | 'panning' | 'zooming' | 'clicking';

// State shape for the map view
interface MapState {
    center: Coordinates;
    zoom: number;
    visibleLocationIds: string[]; // IDs of locations currently in map viewport (optional)
    interactionState: MapInteractionState;
    // Potentially add bounds here if needed globally
    // bounds?: { ne: Coordinates; sw: Coordinates };
}

const initialState: MapState = {
    center: { lat: 39.8283, lng: -98.5795 }, // Default center (e.g., US center)
    zoom: 4, // Default zoom level
    visibleLocationIds: [],
    interactionState: 'idle',
};

const mapSlice = createSlice({
    name: 'map',
    initialState,
    reducers: {
        setCenter: (state, action: PayloadAction<Coordinates>) => {
            state.center = action.payload;
        },
        setZoom: (state, action: PayloadAction<number>) => {
            state.zoom = action.payload;
        },
        setCenterAndZoom: (state, action: PayloadAction<{ center: Coordinates; zoom: number }>) => {
            state.center = action.payload.center;
            state.zoom = action.payload.zoom;
        },
        setVisibleLocationIds: (state, action: PayloadAction<string[]>) => {
            // Simple replacement, could use more complex logic if needed
            state.visibleLocationIds = action.payload;
        },
        setInteractionState: (state, action: PayloadAction<MapInteractionState>) => {
            state.interactionState = action.payload;
        },
        // Reducer to potentially set map bounds
        // setBounds: (state, action: PayloadAction<{ ne: Coordinates; sw: Coordinates }>) => {
        //     state.bounds = action.payload;
        // },
    },
});

// Export actions and reducer
export const { 
    setCenter, 
    setZoom, 
    setCenterAndZoom,
    setVisibleLocationIds, 
    setInteractionState 
} = mapSlice.actions;
export default mapSlice.reducer;

// --- Selectors ---
export const selectMapCenter = (state: RootState) => state.map.center;
export const selectMapZoom = (state: RootState) => state.map.zoom;
export const selectVisibleLocationIds = (state: RootState) => state.map.visibleLocationIds;
export const selectMapInteractionState = (state: RootState) => state.map.interactionState;
// export const selectMapBounds = (state: RootState) => state.map.bounds; 